import React, { useCallback, useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import Login from "../pages/Login";
import { useAuth } from "../../context/AuthContext";
import {
  FaChevronRight, FaLock, FaSuitcaseRolling, FaExchangeAlt,
  FaTimesCircle, FaCheckCircle, FaInfoCircle, FaPlane,
  FaChevronDown, FaChevronUp, FaTimes, FaUser, FaBaby,
  FaChild,
} from "react-icons/fa";
import FlightCard from "./FlightCard";
import RoundTripFlightCard from "./RoundTripFlightCard";
import MultiCityFlightCard from "./MultiCityFlightCard";
import { CurrencyContext } from "../common/CurrencyContext";
import { useBooking } from "../../context/BookingContext";
import { serviceFeeOf } from "../../utils/pricing";
import FareCard from "../ui/FareCard";

interface BookingPopupProps {
  onClose: () => void;
  flightData: any;
  passengerCounts: { ADT: number; CHD?: number; INF?: number };
  departureDate?: string;
  returnDate?: string;
  classOfService: string;
  selectedClass: string;
  selectedOnwardFareTier?: any;
  selectedReturnFareTier?: any;
  selectedFareTierIndex?: number;
  tripType?: "oneway" | "roundtrip" | "multicity";
  multicitySegmentDates?: string[];
}

const BookingPopup: React.FC<BookingPopupProps> = ({
  onClose,
  flightData,
  passengerCounts,
  classOfService,
  selectedClass,
  departureDate,
  returnDate,
  selectedOnwardFareTier,
  selectedReturnFareTier,
  selectedFareTierIndex = 0,
  tripType,
  multicitySegmentDates,
}) => {
  const { user, loading } = useAuth();
  const {
    setSessionId, setRevalId, setFlightData,
    setPassengerCounts: setPassengerCountsInContext,
    setFareRules, setTripType, setDepartureDate,
    setReturnDate, setMulticitySegmentDates,
  } = useBooking();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingContinue, setPendingContinue] = useState(false);
  const [revalidateError, setRevalidateError] = useState<string | null>(null);
  const [revalidateLoading, setRevalidateLoading] = useState(false);
  const [fareOpen, setFareOpen] = useState(true);
  const [policyOpen, setPolicyOpen] = useState(true);
  const navigate = useNavigate();

  const userProfile = { customId: user?.uid || "" };

  const outboundLeg =
    flightData?.legs?.find((l: any) => l.direction === "outbound") ??
    flightData?.legs?.[0];
  const inboundLeg = flightData?.legs?.find((l: any) => l.direction === "inbound");
  const onward = outboundLeg?.segments?.[0];
  const returnSegmentSeg = inboundLeg?.segments?.[0];

  const departureTime =
    outboundLeg?.departure?.dateTime?.match(/T(\d{2}:\d{2})/)?.[1] ?? "-";
  const arrivalTime =
    outboundLeg?.arrival?.dateTime?.match(/T(\d{2}:\d{2})/)?.[1] ?? "-";
  const from = outboundLeg?.departure?.airport?.code ?? "-";
  const to = outboundLeg?.arrival?.airport?.code ?? "-";
  const flightNumber = `${onward?.marketingCarrier?.code ?? ""} ${onward?.marketingCarrier?.flightNumber ?? ""}`;
  const airlineCode =
    flightData?.validatingCarrier?.code ?? flightData?.validatingCarrier ?? "SV";
  const { currency } = useContext(CurrencyContext);

  const isRoundTrip =
    tripType === "roundtrip" ||
    flightData?.tripType === "roundtrip" ||
    flightData?.legs?.some((l: any) => l.direction === "inbound");
  const isMultiCity =
    tripType === "multicity" || flightData?.tripType === "multicity";

  const resolvedFareOption: any = isMultiCity
    ? flightData?.fareOptions?.[selectedFareTierIndex]
    : selectedOnwardFareTier?.fullGroup ?? selectedOnwardFareTier;

  const adtPrice = resolvedFareOption?.passengerPrices?.find(
    (p: any) => p.type === "ADT"
  );
  const chdPrice = resolvedFareOption?.passengerPrices?.find(
    (p: any) => p.type === "CHD"
  );
  const infPrice = resolvedFareOption?.passengerPrices?.find(
    (p: any) => p.type === "INF"
  );
  const fareCurrency = resolvedFareOption?.price?.currency ?? "AED";
  const adtTotal = adtPrice?.total ?? resolvedFareOption?.price?.total ?? 0;

  const cabinClassNameToCode: Record<string, string> = {
    economy: "Y", premiumeconomy: "S", business: "C",
    premiumbusiness: "J", first: "F", premiumfirst: "P",
  };
  const resolvedCabinCode: string | undefined =
    outboundLeg?.segments?.[0]?.bookingInfo?.cabinCode ??
    (resolvedFareOption?.cabinClass
      ? cabinClassNameToCode[
      (resolvedFareOption.cabinClass as string).toLowerCase().replace(/\s+/g, "")
      ]
      : undefined);

  const resolvedFareTier: any = resolvedFareOption
    ? {
      label: resolvedFareOption.name ?? "",
      price: adtTotal,
      currency: fareCurrency,
      baggage:
        resolvedFareOption.baggage?.checked?.description ??
        resolvedFareOption.baggage?.cabin?.description ?? "",
      cancellation:
        resolvedFareOption.refundPolicy?.conditions ??
        (resolvedFareOption.refundPolicy?.refundable
          ? "Refundable"
          : resolvedFareOption.refundPolicy?.cancellationFee
            ? `Non-refundable (fee: ${resolvedFareOption.refundPolicy.cancellationFee.amount} ${fareCurrency})`
            : "Non-refundable"),
      addOns:
        resolvedFareOption.amenities?.map((a: any) => a.description).join(", ") ?? "",
      cabinCode: resolvedCabinCode,
      fullGroup: resolvedFareOption,
    }
    : undefined;

  // ── Helpers ────────────────────────────────────────────────────────────────
  const fmt = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const totalPassengers =
    passengerCounts.ADT + (passengerCounts.CHD ?? 0) + (passengerCounts.INF ?? 0);

  const quotedTotal =
    adtTotal * passengerCounts.ADT +
    (chdPrice?.total ?? 0) * (passengerCounts.CHD ?? 0) +
    (infPrice?.total ?? 0) * (passengerCounts.INF ?? 0);

  const popupServiceFee = serviceFeeOf(flightData, resolvedFareOption);
  /**
   * The fare option's own total when it has one — it is the authoritative
   * per-tier price — otherwise the sum of the passenger rows.
   */
  const fareTotal = resolvedFareOption?.price?.total ?? quotedTotal;

  /**
   * What this screen quotes. Both totals below used to read
   * `resolvedFareOption.price.total` directly and fall back to the computed
   * figure only when it was missing.
   */
  const grandTotal = fareTotal;

  const isRefundable = resolvedFareOption?.refundPolicy?.refundable === true;
  const isChangeable = resolvedFareOption?.changePolicy?.changeable === true;
  const changeFee = resolvedFareOption?.changePolicy?.changeFee;
  const cancelFee = resolvedFareOption?.refundPolicy?.cancellationFee;

  // ── Continue / Revalidate ──────────────────────────────────────────────────
  const handleContinue = useCallback(async () => {
    setRevalidateError(null);
    if (!user) {
      setShowLoginModal(true);
      setPendingContinue(true);
      return;
    }
    setPendingContinue(false);
    setRevalidateLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL ?? ""}/revalidate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            userId: user?.uid ?? "",
            selection: {
              searchSessionId: flightData?.searchSessionId,
              itineraryId: flightData?.id,
              selectedFareTierIndex,
              passengerCounts,
              currency: currency || "AED",
              tripType:
                tripType ||
                flightData?.tripType ||
                (isMultiCity ? "multicity" : isRoundTrip ? "roundtrip" : "oneway"),
              departureDate:
                typeof departureDate === "string" ? departureDate : undefined,
              returnDate:
                typeof returnDate === "string" ? returnDate : undefined,
              multicitySegmentDates:
                tripType === "multicity" || flightData?.tripType === "multicity"
                  ? multicitySegmentDates
                  : undefined,
            },
          }),
          credentials: "include",
        }
      );

      if (res.status === 200) {
        const revalidatedData = await res.json();
        if (revalidatedData.sessionId && revalidatedData.revalId) {
          setSessionId(revalidatedData.sessionId);
          setRevalId(revalidatedData.revalId);
        }
        setFlightData(revalidatedData);
        setPassengerCountsInContext(passengerCounts);
        setFareRules(null);
        const resolvedTripType: "oneway" | "roundtrip" | "multicity" =
          tripType === "multicity" || revalidatedData?.tripType === "multicity"
            ? "multicity"
            : revalidatedData?.tripType ??
            (revalidatedData?.legs?.some((l: any) => l.direction === "inbound")
              ? "roundtrip"
              : "oneway");
        setTripType(resolvedTripType);
        const depOnly =
          typeof departureDate === "string"
            ? departureDate.split("T")[0]
            : departureDate ?? "";
        const retOnly =
          typeof returnDate === "string"
            ? returnDate.split("T")[0]
            : returnDate ?? undefined;
        setDepartureDate(depOnly);
        setReturnDate(retOnly ?? undefined);
        setMulticitySegmentDates(
          resolvedTripType === "multicity" && Array.isArray(multicitySegmentDates)
            ? multicitySegmentDates.map((d) =>
              typeof d === "string" ? d.split("T")[0] : d
            )
            : undefined
        );
        navigate("/passenger-details", {
          state: {
            flightData: revalidatedData,
            passengerCounts,
            departureDate,
            returnDate,
            multicitySegmentDates:
              resolvedTripType === "multicity" ? multicitySegmentDates : undefined,
            tripType: resolvedTripType,
            classOfService,
            selectedClass,
          },
        });
      } else {
        const errorData = await res.json();
        setRevalidateError(
          errorData?.error ||
          "Revalidation failed. Please try again or select another flight."
        );
      }
    } catch (error: any) {
      setRevalidateError(
        error?.message || "Network error during revalidation. Please try again."
      );
    } finally {
      setRevalidateLoading(false);
    }
  }, [
    user, userProfile, flightData, selectedClass, passengerCounts,
    classOfService, departureDate, returnDate, selectedOnwardFareTier,
    selectedReturnFareTier, selectedFareTierIndex, tripType,
    multicitySegmentDates, currency, navigate, setSessionId, setRevalId,
    setFareRules, setTripType, setDepartureDate, setReturnDate,
    setMulticitySegmentDates, setFlightData, setPassengerCountsInContext,
  ]);

  useEffect(() => {
    if (user && pendingContinue && !showLoginModal) handleContinue();
  }, [user, pendingContinue, showLoginModal, handleContinue]);

  const handleLoginModalClose = () => {
    setShowLoginModal(false);
    setPendingContinue(false);
  };

  if (loading) return <div>Loading...</div>;

  // ── UI helpers ─────────────────────────────────────────────────────────────
  const SectionHeader = ({
    icon,
    title,
    open,
    onToggle,
  }: {
    icon: React.ReactNode;
    title: string;
    open: boolean;
    onToggle: () => void;
  }) => (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-5 py-3.5 bg-white rounded-xl mb-0 border border-gray-100 hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center gap-2.5 text-primary font-semibold text-sm">
        <span className="text-primary">{icon}</span>
        {title}
      </div>
      {open ? (
        <FaChevronUp size={12} className="text-gray-400" />
      ) : (
        <FaChevronDown size={12} className="text-gray-400" />
      )}
    </button>
  );

  const PolicyRow = ({
    label,
    value,
    ok,
  }: {
    label: string;
    value: string;
    ok: boolean;
  }) => (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-600">{label}</span>
      <span
        className={`flex items-center gap-1.5 text-sm font-medium ${ok ? "text-green-600" : "text-red-500"
          }`}
      >
        {ok ? <FaCheckCircle size={12} /> : <FaTimesCircle size={12} />}
        {value}
      </span>
    </div>
  );

  const PaxRow = ({
    icon,
    label,
    count,
    total,
  }: {
    icon: React.ReactNode;
    label: string;
    count: number;
    total: number;
  }) => (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <span className="text-gray-400">{icon}</span>
        {count} × {label}
      </div>
      <span className="text-sm font-medium text-gray-800">
        {fareCurrency} {fmt(total)}
      </span>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center overflow-y-auto p-0 sm:p-4">
      <div className="relative w-full sm:max-w-5xl max-h-screen sm:max-h-[92vh] flex flex-col bg-[#F2F6FF] sm:rounded-2xl overflow-hidden shadow-2xl">

        {/* ── Header ── */}
        <div className="bg-primary px-5 sm:px-8 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
              <FaPlane className="text-white text-sm" />
            </div>
            <div>
              <div className="text-white font-bold text-base leading-tight">
                Review your booking
              </div>
              <div className="text-blue-200 text-xs mt-0.5">
                {from} → {to} ·{" "}
                {totalPassengers} passenger{totalPassengers !== 1 ? "s" : ""} ·{" "}
                {classOfService}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <FaTimes className="text-white text-sm" />
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 sm:px-8 py-5 space-y-4">

            {/* ── Flight card strip ── */}
            <div className="rounded-xl overflow-hidden border border-gray-100 shadow-sm">
              {isMultiCity ? (
                <MultiCityFlightCard
                  itinerary={flightData}
                  selectedFareTierIndex={selectedFareTierIndex}
                  showFareSelection={false}
                  expandedByDefault={true}
                  disableExpansion={false}
                  disableResetOnCollapse={true}
                  showBookButton={false}
                  classOfService={classOfService}
                  selectedClass={selectedClass}
                />
              ) : isRoundTrip ? (
                <RoundTripFlightCard
                  amount={flightData?.pricingInfo?.total ?? 0}
                  currency={flightData?.pricingInfo?.currency ?? "USD"}
                  baseFare={flightData?.pricingInfo?.baseFare ?? 0}
                  taxes={flightData?.pricingInfo?.taxes ?? 0}
                  classOfService={classOfService}
                  selectedClass={selectedClass}
                  flightData={flightData}
                  onward={{
                    flightNumber,
                    airlineName: airlineCode,
                    airlineLogo: `/assets/airlines/${airlineCode}.png`,
                    from: outboundLeg?.departure?.airport?.code ?? "-",
                    to: outboundLeg?.arrival?.airport?.code ?? "-",
                    departure: departureTime,
                    arrival: arrivalTime,
                  }}
                  returnSegment={{
                    flightNumber: `${returnSegmentSeg?.marketingCarrier?.code ?? ""} ${returnSegmentSeg?.marketingCarrier?.flightNumber ?? ""}`,
                    airlineName: airlineCode,
                    airlineLogo: `/assets/airlines/${airlineCode}.png`,
                    from: inboundLeg?.departure?.airport?.code ?? "-",
                    to: inboundLeg?.arrival?.airport?.code ?? "-",
                    departure:
                      inboundLeg?.departure?.dateTime?.match(/T(\d{2}:\d{2})/)?.[1] ?? "-",
                    arrival:
                      inboundLeg?.arrival?.dateTime?.match(/T(\d{2}:\d{2})/)?.[1] ?? "-",
                  }}
                  selectedOnwardFareTier={resolvedFareTier}
                  selectedReturnFareTier={resolvedFareTier}
                  disableExpansion={true}
                />
              ) : (
                <FlightCard
                  onward={{
                    flightNumber,
                    airlineName: airlineCode,
                    airlineLogo: `/assets/airlines/${airlineCode}.png`,
                    from,
                    to,
                    departure: departureTime,
                    arrival: arrivalTime,
                  }}
                  amount={flightData?.pricingInfo?.total ?? 0}
                  baseFare={flightData?.pricingInfo?.baseFare ?? 0}
                  taxes={flightData?.pricingInfo?.taxes ?? 0}
                  currency={flightData?.pricingInfo?.currency ?? "USD"}
                  classType={classOfService}
                  selectedClass={selectedClass}
                  flightData={flightData}
                  fareTiers={[]}
                  selectedFareTier={resolvedFareTier}
                  disableExpansion={true}
                />
              )}
            </div>

            {/* ── Fare badge ── */}
            {resolvedFareOption && (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <FaSuitcaseRolling className="text-primary text-base" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">
                        {resolvedFareOption.name}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {resolvedFareTier?.baggage || "Cabin bag included"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`text-xs px-2.5 py-1 rounded-full font-medium ${isRefundable
                          ? "bg-green-50 text-green-700 border border-green-200"
                          : "bg-red-50 text-red-600 border border-red-200"
                        }`}
                    >
                      {isRefundable ? "Refundable" : "Non-refundable"}
                    </span>
                    <span
                      className={`text-xs px-2.5 py-1 rounded-full font-medium ${isChangeable
                          ? "bg-blue-50 text-blue-700 border border-blue-200"
                          : "bg-gray-50 text-gray-500 border border-gray-200"
                        }`}
                    >
                      {isChangeable ? "Date change allowed" : "Non-changeable"}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* ── 2-col grid: fare + policy ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* Fare summary */}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader
                  icon={<FaInfoCircle size={13} />}
                  title="Fare Summary"
                  open={fareOpen}
                  onToggle={() => setFareOpen((p) => !p)}
                />
                {fareOpen && (
                  <div className="px-5 pb-4 pt-2 space-y-0">
                    {passengerCounts.ADT > 0 && (
                      <PaxRow
                        icon={<FaUser size={11} />}
                        label="Adult"
                        count={passengerCounts.ADT}
                        total={adtTotal * passengerCounts.ADT}
                      />
                    )}
                    {(passengerCounts.CHD ?? 0) > 0 && (
                      <PaxRow
                        icon={<FaChild size={11} />}
                        label="Child"
                        count={passengerCounts.CHD ?? 0}
                        total={(chdPrice?.total ?? 0) * (passengerCounts.CHD ?? 0)}
                      />
                    )}
                    {(passengerCounts.INF ?? 0) > 0 && (
                      <PaxRow
                        icon={<FaBaby size={11} />}
                        label="Infant"
                        count={passengerCounts.INF ?? 0}
                        total={(infPrice?.total ?? 0) * (passengerCounts.INF ?? 0)}
                      />
                    )}

                    {/* Breakdown */}
                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5">
                      {resolvedFareOption?.price?.baseFare != null && (
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Base fare</span>
                          <span>
                            {fareCurrency} {resolvedFareOption.price.baseFare.toLocaleString()}
                          </span>
                        </div>
                      )}
                      {resolvedFareOption?.price?.taxes != null && (
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Taxes &amp; fees</span>
                          <span>
                            {fareCurrency} {resolvedFareOption.price.taxes.toLocaleString()}
                          </span>
                        </div>
                      )}

                      {/* The agency's margin, shown as part of what the
                          total is made of. */}
                      {popupServiceFee > 0 && (
                        <div className="flex justify-between text-xs text-primary">
                          <span>Service fee</span>
                          <span>
                            {fareCurrency} {popupServiceFee.toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Total */}
                    <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between items-baseline">
                      <span className="font-bold text-gray-900 text-sm">Total payable</span>
                      <span className="font-bold text-primary text-xl">
                        {fareCurrency} {fmt(grandTotal)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 text-right -mt-1">
                      Incl. taxes for all passengers
                    </div>
                  </div>
                )}
              </div>

              {/* Policies */}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader
                  icon={<FaExchangeAlt size={12} />}
                  title="Fare Rules &amp; Policies"
                  open={policyOpen}
                  onToggle={() => setPolicyOpen((p) => !p)}
                />
                {policyOpen && (
                  <div className="px-5 pb-4 pt-2 space-y-0">
                    <PolicyRow
                      label="Cancellation"
                      ok={isRefundable}
                      value={
                        isRefundable
                          ? "Refundable"
                          : cancelFee
                            ? `Fee ${fareCurrency} ${cancelFee.amount}`
                            : "Non-refundable"
                      }
                    />
                    <PolicyRow
                      label="Date change"
                      ok={isChangeable}
                      value={
                        isChangeable
                          ? changeFee
                            ? `Fee ${fareCurrency} ${changeFee.amount}`
                            : "Free"
                          : "Not allowed"
                      }
                    />
                    <PolicyRow
                      label="Cabin baggage"
                      ok={true}
                      value={
                        resolvedFareOption?.baggage?.cabin?.description ??
                        "1 cabin bag 7 kg"
                      }
                    />
                    <PolicyRow
                      label="Checked baggage"
                      ok={
                        (resolvedFareOption?.baggage?.checked?.pieces ?? 0) > 0
                      }
                      value={
                        resolvedFareOption?.baggage?.checked?.description ??
                        "No checked baggage"
                      }
                    />
                    {resolvedFareOption?.refundPolicy?.conditions && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="text-xs text-gray-400 flex gap-1.5">
                          <FaInfoCircle size={11} className="shrink-0 mt-0.5 text-gray-300" />
                          {resolvedFareOption.refundPolicy.conditions}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ── Error ── */}
            {revalidateError && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-2.5">
                <FaTimesCircle className="text-red-500 mt-0.5 shrink-0" size={14} />
                <div className="text-sm text-red-700">{revalidateError}</div>
              </div>
            )}

            {/* bottom padding so content clears sticky footer */}
            <div className="h-2" />
          </div>
        </div>

        {/* ── Sticky footer CTA ── */}
        <div className="flex-shrink-0 bg-white border-t border-gray-100 px-5 sm:px-8 py-4 flex items-center justify-between gap-4 flex-wrap shadow-lg">
          <div>
            <div className="text-xs text-gray-500 mb-0.5">Total amount</div>
            <div className="text-xl font-bold text-primary">
              {fareCurrency} {fmt(grandTotal)}
            </div>
            <div className="text-xs text-gray-400">Incl. all taxes &amp; fees</div>
          </div>

          <button
            onClick={handleContinue}
            disabled={revalidateLoading}
            className="flex items-center gap-2.5 bg-primary hover:bg-blue-800 disabled:bg-blue-300 text-white font-bold px-7 py-3.5 rounded-xl transition-all shadow-md hover:shadow-lg active:scale-[0.98] text-sm"
          >
            {revalidateLoading ? (
              <>
                <svg
                  className="animate-spin w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  />
                </svg>
                Checking availability...
              </>
            ) : (
              <>
                <FaLock size={12} />
                Continue to passenger details
                <FaChevronRight size={11} />
              </>
            )}
          </button>
        </div>
      </div>

      {showLoginModal && (
        <Login
          onSuccess={() => setShowLoginModal(false)}
          onClose={handleLoginModalClose}
        />
      )}
    </div>
  );
};

export default BookingPopup;